import { R2Config } from '../config/r2-config.js';

export class IndexedDBService {
  constructor() {
    this.db = null;
    this.dbName = R2Config.cache.dbName;
    this.dbVersion = R2Config.cache.dbVersion;
    this.stores = { ...R2Config.cache.stores };
    this.upgradeAttempts = 0;
    this.maxUpgradeAttempts = 2;
    this.isInitialized = false;
    this.initializationPromise = null;
    this.hasLoggedInProgress = false;
    
    // Ensure required stores exist
    this.stores.scenarioImages = this.stores.scenarioImages || 'scenarioImages';
    this.stores.imageUrls = this.stores.imageUrls || 'imageUrls';
    this.stores.locations = this.stores.locations || 'locations';
    this.stores.tagMappings = this.stores.tagMappings || 'tag-mappings';
    
    // TTL config (6h default)
    this.ttlConfig = {
      [this.stores.imageUrls]: 6 * 3600000,
      [this.stores.scenarioImages]: 6 * 3600000,
      [this.stores.characters]: 6 * 3600000,
      [this.stores.characterDetails]: 6 * 3600000,
      [this.stores.scenarios]: 6 * 3600000,
      [this.stores.tagMappings]: 24 * 3600000,
      default: R2Config.cache.maxAge || 3600000
    };
  }
  
  async init() {
    if (this.initializationPromise) {
      if (!this.hasLoggedInProgress) {
        console.log('IndexedDBService.init: Already initializing');
        this.hasLoggedInProgress = true;
      }
      return this.initializationPromise;
    }
    
    if (this.isInitialized && this.db) {
      const missingStores = this.checkMissingStores();
      if (missingStores.length === 0) {
        return this.db;
      }
      
      console.log('Missing stores detected:', missingStores);
      this.db.close();
      this.db = null;
      this.isInitialized = false;
      
      if (this.upgradeAttempts < this.maxUpgradeAttempts) {
        this.upgradeAttempts++;
        this.initializationPromise = this.forceUpgrade().then(() => this.init());
        return this.initializationPromise;
      }
      
      return null;
    }
    
    const currentVersion = await this.getCurrentVersion();
    if (currentVersion > this.dbVersion) {
      this.dbVersion = currentVersion;
      R2Config.cache.dbVersion = currentVersion;
    }
    
    this.initializationPromise = new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.dbVersion);
      
      request.onerror = (event) => {
        if (event.target.error.name === 'VersionError') {
          if (this.upgradeAttempts < this.maxUpgradeAttempts) {
            this.upgradeAttempts++;
            this.forceUpgrade().then(() => this.init()).then(resolve).catch(reject);
            return;
          }
        }
        reject(event.target.error);
      };
      
      request.onsuccess = (event) => {
        this.db = event.target.result;
        const missingStores = this.checkMissingStores();
        
        if (missingStores.length > 0 && this.upgradeAttempts < this.maxUpgradeAttempts) {
          this.upgradeAttempts++;
          this.db.close();
          this.db = null;
          this.forceUpgrade().then(() => this.init()).then(resolve).catch(reject);
          return;
        }
        
        this.isInitialized = true;
        resolve(this.db);
      };
      
      request.onupgradeneeded = (event) => {
        const db = event.target.result;
        this.removeDeprecatedStores(db);
        this.createStores(db);
      };
    });
    
    return this.initializationPromise;
  }

  removeDeprecatedStores(db) {
    if (db.objectStoreNames.contains('relationships')) {
      db.deleteObjectStore('relationships');
    }
  }
  
  createStores(db) {
    const stores = [
      { name: this.stores.characters, keyPath: 'id', index: 'updatedAt' },
      { name: this.stores.characterDetails, keyPath: 'id', index: 'updatedAt' },
      { name: this.stores.scenarios, keyPath: 'id', indexes: ['updatedAt', 'tokenExpiry'] },
      { name: this.stores.scenarioImages, keyPath: 'id', indexes: ['updatedAt', 'tokenExpiry'] },
      { name: this.stores.imageUrls, keyPath: 'key', index: 'updatedAt' },
      { name: this.stores.locations, keyPath: 'id', index: 'updatedAt' },
      { name: this.stores.tagMappings, keyPath: 'key', index: 'updatedAt' }
    ];
    
    stores.forEach(store => {
      if (!db.objectStoreNames.contains(store.name)) {
        const objectStore = db.createObjectStore(store.name, { keyPath: store.keyPath });
        
        if (store.indexes) {
          store.indexes.forEach(index => {
            objectStore.createIndex(index, index, { unique: false });
          });
        } else if (store.index) {
          objectStore.createIndex(store.index, store.index, { unique: false });
        }
      }
    });
    
    if (!db.objectStoreNames.contains('metadata')) {
      db.createObjectStore('metadata', { keyPath: 'key' });
    }
  }
  
  checkMissingStores() {
    if (!this.db) return ['No database connection'];
    
    const requiredStores = [
      this.stores.scenarioImages, 
      this.stores.imageUrls, 
      this.stores.locations, 
      this.stores.tagMappings
    ].filter(Boolean); // Filter out undefined stores
    
    return requiredStores.filter(storeName => !this.db.objectStoreNames.contains(storeName));
  }
  
  async getCurrentVersion() {
    return new Promise((resolve) => {
      const request = indexedDB.open(this.dbName);
      
      request.onsuccess = (event) => {
        const version = event.target.result.version;
        event.target.result.close();
        resolve(version);
      };
      
      request.onerror = () => resolve(0);
      request.onupgradeneeded = (event) => {
        event.target.result.close();
        resolve(0);
      };
    });
  }
  
  async forceUpgrade() {
    if (this.db) {
      this.db.close();
      this.db = null;
    }
    
    this.isInitialized = false;
    this.initializationPromise = null;
    
    const currentVersion = await this.getCurrentVersion();
    this.dbVersion = Math.max(currentVersion + 1, this.dbVersion);
    
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.dbVersion);
      
      request.onerror = (event) => reject(event.target.error);
      
      request.onupgradeneeded = (event) => {
        const db = event.target.result;
        this.removeDeprecatedStores(db);
        this.createStores(db);
      };
      
      request.onsuccess = () => {
        this.isInitialized = true;
        R2Config.cache.dbVersion = this.dbVersion;
        resolve();
      };
    });
  }
  
  async storeData(storeName, data) {
    await this.init();
    
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction(storeName, 'readwrite');
      const store = transaction.objectStore(storeName);
      const timestamp = Date.now();
      
      transaction.onerror = (event) => reject(event.target.error);
      transaction.oncomplete = () => resolve();
      
      const items = Array.isArray(data) ? data : [data];
      items.forEach(item => {
        store.put({ ...item, updatedAt: timestamp });
      });
      
      const metadataStore = transaction.db.transaction('metadata', 'readwrite').objectStore('metadata');
      metadataStore.put({ key: `lastFetch_${storeName}`, timestamp });
    });
  }
  
  async getAllData(storeName) {
    await this.init();
    
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction(storeName, 'readonly');
      const request = transaction.objectStore(storeName).getAll();
      
      request.onerror = (event) => reject(event.target.error);
      request.onsuccess = () => resolve(request.result);
    });
  }
  
  async getById(storeName, id) {
    await this.init();
    
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction(storeName, 'readonly');
      const request = transaction.objectStore(storeName).get(id);
      
      request.onerror = (event) => reject(event.target.error);
      request.onsuccess = () => resolve(request.result);
    });
  }
  
  async isDataStale(storeName) {
    await this.init();
    
    return new Promise((resolve, reject) => {
      const request = this.db.transaction('metadata', 'readonly').objectStore('metadata').get(`lastFetch_${storeName}`);
      
      request.onerror = (event) => reject(event.target.error);
      request.onsuccess = () => {
        if (!request.result) return resolve(true);
        
        const lastFetch = request.result.timestamp;
        const now = Date.now();
        const ttl = this.ttlConfig[storeName] || this.ttlConfig.default;
        
        if (storeName === this.stores.scenarios && request.result.tokenExpiry) {
          if (request.result.tokenExpiry <= now) return resolve(true);
        }
        
        resolve(now - lastFetch > ttl);
      };
    });
  }
  
  async clearStore(storeName) {
    await this.init();
    
    return new Promise((resolve, reject) => {
      const request = this.db.transaction(storeName, 'readwrite').objectStore(storeName).clear();
      
      request.onerror = (event) => reject(event.target.error);
      request.onsuccess = () => resolve();
    });
  }
  
  async storeScenarios(scenarios, token, tokenExpiry) {
    try {
      await this.init();
      
      if (!this.db.objectStoreNames.contains(this.stores.scenarios)) {
        await this.forceUpgrade();
      }
      
      return new Promise((resolve, reject) => {
        const transaction = this.db.transaction(this.stores.scenarios, 'readwrite');
        const store = transaction.objectStore(this.stores.scenarios);
        const timestamp = Date.now();
        
        transaction.onerror = (event) => reject(event.target.error);
        transaction.oncomplete = () => resolve(true);
        
        scenarios.forEach(scenario => {
          if (!scenario.id) return;
          store.put({ ...scenario, updatedAt: timestamp });
        });
        
        const metadataStore = transaction.db.transaction('metadata', 'readwrite').objectStore('metadata');
        metadataStore.put({ key: `lastFetch_${this.stores.scenarios}`, timestamp });
      });
    } catch (error) {
      if (error.name === 'NotFoundError') {
        await this.forceUpgrade();
        return this.storeScenarios(scenarios);
      }
      return false;
    }
  }
  
  async getScenario(id) {
    try {
      await this.init();
      return await this.getById(this.stores.scenarios, id) || null;
    } catch (error) {
      console.error(`Error getting scenario ${id}:`, error);
      return null;
    }
  }
  
  async getAllScenarios(includeExpired = false) {
    try {
      await this.init();
      return await this.getAllData(this.stores.scenarios);
    } catch (error) {
      console.error('Error getting all scenarios:', error);
      return [];
    }
  }
  
  async getScenarioImageUrls(ids) {
    try {
      await this.init();
      const results = {};
      
      let WORKER_URL = '';
      try {
        const module = await import('../js/config.js');
        WORKER_URL = module.WORKER_URL || '';
      } catch {}
      
      for (const id of ids) {
        const scenario = await this.getById(this.stores.scenarios, id);
        if (scenario?.imageUrl) {
          let imageUrl = scenario.imageUrl;
          if (imageUrl.startsWith('/') && WORKER_URL) {
            imageUrl = `${WORKER_URL}${imageUrl}`;
          }
          results[id] = imageUrl;
        }
      }
      
      return results;
    } catch (error) {
      console.error('Error getting scenario image URLs:', error);
      return {};
    }
  }
  
  async storeImageBlob(id, imageBlob) {
    try {
      await this.init();
      
      if (!this.db.objectStoreNames.contains(this.stores.scenarioImages)) {
        await this.forceUpgrade();
      }
      
      return new Promise((resolve, reject) => {
        const transaction = this.db.transaction(this.stores.scenarioImages, 'readwrite');
        const store = transaction.objectStore(this.stores.scenarioImages);
        const timestamp = Date.now();
        
        transaction.onerror = (event) => reject(event.target.error);
        transaction.oncomplete = () => resolve(true);
        
        store.put({ id, imageBlob, updatedAt: timestamp });
      });
    } catch (error) {
      console.error('Error in storeImageBlob:', error);
      return false;
    }
  }
  
  async getImageBlob(id) {
    try {
      await this.init();
      
      if (!this.db.objectStoreNames.contains(this.stores.scenarioImages)) {
        await this.forceUpgrade();
        await this.init();
      }
      
      return new Promise((resolve, reject) => {
        const transaction = this.db.transaction(this.stores.scenarioImages, 'readonly');
        const request = transaction.objectStore(this.stores.scenarioImages).get(id);
        
        request.onerror = (event) => reject(event.target.error);
        request.onsuccess = () => {
          const data = request.result;
          if (!data?.imageBlob) {
            resolve(null);
            return;
          }
          
          resolve({
            blob: data.imageBlob,
            url: URL.createObjectURL(data.imageBlob)
          });
        };
      });
    } catch (error) {
      console.error(`Error in getImageBlob for scenario ${id}:`, error);
      return null;
    }
  }
  
  async getScenarioImageBlobs(ids) {
    try {
      await this.init();
      
      if (!this.db.objectStoreNames.contains(this.stores.scenarioImages)) {
        await this.forceUpgrade();
        await this.init();
      }
      
      const results = {};
      
      for (const id of ids) {
        try {
          const result = await this.getImageBlob(id);
          if (result?.url) {
            results[id] = result.url;
          }
        } catch (err) {
          // Continue with other IDs
        }
      }
      
      return results;
    } catch (error) {
      console.error('Error getting scenario image blobs:', error);
      return {};
    }
  }
}
